import { useRewardsState } from '../../../coordinators/rewards/hooks/useRewardsState';
import type { CatalogueSku } from '../../../services/rewards/types';
import { DiscordSupportLink } from './RewardsErrorStates';
import { PointsBadge, RewardIcon } from './RewardIcon';

interface CatalogueListProps {
  onSelectItem: (skuId: string) => void;
  onBack: () => void;
}

type TileState = 'can-redeem' | 'need-more' | 'out-of-stock' | 'coming-soon';

function getTileState(item: CatalogueSku, userBalance: number): TileState {
  if (item.comingSoon) return 'coming-soon';
  if (!item.inStock) return 'out-of-stock';
  if (userBalance >= item.pointCost) return 'can-redeem';
  return 'need-more';
}

function tileLabel(state: TileState): string | null {
  switch (state) {
    case 'can-redeem': return 'Can redeem';
    case 'need-more': return 'Need more tokens';
    case 'out-of-stock': return 'Out of stock';
    case 'coming-soon': return 'Coming soon';
  }
}

function tileLabelColor(state: TileState): string {
  switch (state) {
    case 'can-redeem': return 'text-[#6EE7B7]';
    case 'need-more': return 'text-[#FCA5A5]';
    case 'out-of-stock': return 'text-[#FCA5A5]';
    case 'coming-soon': return 'text-[#FFFFFF]/70';
  }
}

export const CatalogueList = ({ onSelectItem, onBack }: CatalogueListProps) => {
  const { profile, catalogue, catalogueLoading, catalogueError, restockCountdown, refreshCatalogue } = useRewardsState();

  const userBalance = profile?.pointsBalance ?? 0;

  if (catalogueLoading && catalogue.length === 0) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <p className='animate-pulse text-sm text-[#FFFFFF]/80'>Loading catalogue...</p>
      </div>
    );
  }

  if (catalogueError && catalogue.length === 0) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-3 px-4'>
        <p className='text-sm text-[#FCA5A5]'>Failed to load catalogue</p>
        <button type='button' onClick={refreshCatalogue}
          className='rounded-lg border border-[#FFFFFF]/15 bg-[#FFFFFF]/5 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-80'>
          Retry
        </button>
        <DiscordSupportLink />
      </div>
    );
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      {/* Back row */}
      <button type='button' onClick={onBack}
        className='mb-3 shrink-0 self-start border-none bg-transparent p-0 text-sm font-semibold text-[#FFFFFF]/90 transition-opacity hover:opacity-80'>
        &larr; Hub
      </button>

      {/* Header row */}
      <div className='mb-[14px] flex shrink-0 items-center'>
        <h2 className='flex-1 text-[18px] font-semibold text-white'>Catalog</h2>
        <PointsBadge balance={userBalance} />
      </div>

      {/* Scrollable tile grid */}
      <div className='hr-scroll min-h-0 flex-1 overflow-y-auto'>
        {catalogue.length === 0 ? (
          <p className='py-8 text-center text-sm text-[#FFFFFF]/80'>No items available</p>
        ) : (
          <div className='grid grid-cols-2 gap-2'>
            {catalogue.map((item) => {
              const state = getTileState(item, userBalance);
              const label = tileLabel(state);
              const isOos = state === 'out-of-stock';
              const isComingSoon = state === 'coming-soon';
              const dimmed = isOos || isComingSoon;
              const isClickable = !isOos && !isComingSoon;

              return (
                <button
                  key={item.skuId}
                  type='button'
                  onClick={() => isClickable && onSelectItem(item.skuId)}
                  disabled={!isClickable}
                  className={`flex flex-col text-left ${!isClickable ? 'cursor-default' : ''}`}
                  style={{
                    borderRadius: 8,
                    padding: 8,
                    border: isComingSoon
                      ? '1px dashed rgba(255,255,255,0.15)'
                      : '1px solid rgba(255,255,255,0.15)',
                    opacity: dimmed ? 0.6 : 1,
                    transition: 'border-color 0.15s',
                  }}
                >
                  <div className='relative w-full overflow-hidden rounded-[6px] bg-[#FFFFFF]/3' style={{ aspectRatio: '3/2' }}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} className='absolute inset-0 h-full w-full object-cover' />
                    ) : (
                      <div className='flex h-full w-full items-center justify-center'>
                        <span className='text-[10px] text-[#FFFFFF]/20'>Art</span>
                      </div>
                    )}
                  </div>
                  <p className='mt-1 line-clamp-2 text-[11px] font-semibold text-white'>{item.name}</p>
                  {!isComingSoon && (
                    <span className='mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-semibold text-[#FFFFFF]/90'>
                      <RewardIcon size={11} />
                      {item.pointCost.toLocaleString()}
                    </span>
                  )}
                  {label && (
                    <p className={`mt-0.5 text-[9px] font-semibold ${tileLabelColor(state)}`}>{label}</p>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className='shrink-0 text-center' style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
        <p className='text-[10px] text-[#FFFFFF]/70'>
          Catalog restock in <span className='font-medium text-[#FFFFFF]/90'>{restockCountdown?.label || '—'}</span>
        </p>
        {restockCountdown?.localDate && <p className='text-[9px] text-[#FFFFFF]/60'>{restockCountdown.localDate}</p>}
      </div>
    </div>
  );
};
